import { featureTo2Lines } from "fe-common/logic";
import * as React from "react";
import { Dispatch, useEffect, useRef, useState } from "react";
import { Button, styled, Theme } from "@mui/material";
import { DDClinicalReasoning } from "fe-common/graphql/dao";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { RosebudColorMap } from "./rosebud/rosebudColorMap";
import { MobileView } from "../../util/aKaunGeneralUtil";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { SubTypesMenu } from "./miniDisease/subTypesMenu";
import { MobileOnly } from "./util/mobileOnly";

const SUBTYPES_LINE_HEIGHT = 48;
const SUBTYPES_SELECTED_BORDER = 4;

const SubtypesList = styled("div")(({ theme }: { theme?: Theme }) => ({
  display: "flex",

  flexFlow: "row",
  justifyContent: "stretch",
  flexWrap: "wrap",
  overflow: "hidden",
}));

const SubTypesButton = styled(Button)(
  ({ theme, selected }: { theme?: any; selected: boolean }) => ({
    padding: "8px",
    color: "white",
    borderRadius: "0",
    borderBottom: selected
      ? `${SUBTYPES_SELECTED_BORDER}px solid ${KahunColorMap.PrimaryDim}`
      : "",
    fontSize: "1rem",
    [theme.breakpoints.down(MobileView)]: {
      fontSize: "14px",
    },
    textTransform: "unset",
  }),
);

const SeeMoreLink = styled("div")({
  alignSelf: "flex-end",
  cursor: "pointer",
  color: "white",
});

const subTypeStyle = {
  display: "inline",
  fontSize: "11px",
};

const SubType = styled("span")(
  ({ theme, selected }: { theme?: any; selected: boolean }) => ({
    ...subTypeStyle,
    textDecoration: selected ? "underline" : "none",
    padding: "6px",
    fontSize: "14px",

    [theme.breakpoints.down(MobileView)]: {
      fontSize: "14px",
    },

    border: `1px solid ${RosebudColorMap.green1}`,
    borderRadius: "5px",
    marginRight: "8px",
    marginBottom: "8px",
  }),
);

const SubTypesTitle = styled("div")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  marginTop: "20px",
  marginLeft: "7px",
  color: "white",
  [theme.breakpoints.down(MobileView)]: {
    marginTop: "0px",
  },
  marginBottom: "8px",
}));

const SubTypesWrapperView = styled("div")(({ theme }: { theme?: Theme }) => ({
  display: "flex",
  flexFlow: "column",
  marginBottom: "20px",
}));

interface SubTypesProps {
  crs: DDClinicalReasoning[];
  filteredId: string;
  selectedCR?: DDClinicalReasoning;
  setSelectedCR?: Dispatch<DDClinicalReasoning>;
}

export const SubTypes = ({
  crs,
  filteredId,
  selectedCR,
  setSelectedCR,
}: SubTypesProps) => {
  const [enableShowMore, setEnableShowMore] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const subTypesContainer = useRef(null);
  const firstEl = useRef(null);
  const lastEl = useRef(null);

  const setFirstLineMaxHeight = (height: string) => {
    if (
      subTypesContainer &&
      subTypesContainer.current &&
      subTypesContainer.current.style
    ) {
      subTypesContainer.current.style.maxHeight = height;
    }
  };

  useEffect(() => {
    const firstElHeight = firstEl.current?.getBoundingClientRect?.().height;
    if (firstElHeight && !showMore) {
      setFirstLineMaxHeight(`${firstElHeight + SUBTYPES_SELECTED_BORDER}px`);
    } else {
      setFirstLineMaxHeight("");
    }
    if (
      lastEl.current?.getBoundingClientRect?.().top -
        subTypesContainer.current?.getBoundingClientRect?.().top >=
      SUBTYPES_LINE_HEIGHT - SUBTYPES_SELECTED_BORDER
    ) {
      setEnableShowMore(true);
    }
  }, [subTypesContainer, lastEl, showMore]);

  const subTypes = crs.filter((cr) => cr.disease[0].id !== filteredId);
  const showSubTypes = subTypes && subTypes.length;

  if (!showSubTypes) return <span></span>;

  const onShowMore = () => {
    setShowMore(!showMore);
  };

  const onSubtypeSelect = (cr, subType) => {
    cr !== selectedCR ? setSelectedCR(cr) : setSelectedCR(null);
    evt({
      category: CATEGORY.AGENT_DISEASE_CARD,
      action: cr !== selectedCR ? ACTION.SELECT_SUBTYPE : ACTION.REMOVE_SUBTYPE,
      label: subType,
    });
  };

  // If there are no sub-types don't render any thing
  if (!subTypes?.length) return <></>;

  return (
    <SubTypesWrapperView>
      {false && (
        <>
          <SubTypesTitle>Subtypes</SubTypesTitle>
          <SubtypesList ref={subTypesContainer}>
            {subTypes.map((cr, index) => {
              const subTypeFeature = cr.disease[0];
              const [subType] = featureTo2Lines(subTypeFeature);
              const lastElRef = index === subTypes.length - 1 ? lastEl : null;
              return (
                <React.Fragment key={index}>
                  {setSelectedCR ? (
                    <SubTypesButton
                      ref={!index ? firstEl : lastElRef}
                      selected={cr === selectedCR}
                      onClick={() => onSubtypeSelect(cr, subType)}
                    >
                      {subType}
                    </SubTypesButton>
                  ) : (
                    <SubType selected={cr === selectedCR}>{subType}</SubType>
                  )}
                </React.Fragment>
              );
            })}
          </SubtypesList>
        </>
      )}

      {enableShowMore && (
        <SeeMoreLink onClick={onShowMore}>
          {`Show ${showMore ? "Less" : "More"}`}
        </SeeMoreLink>
      )}

      <MobileOnly>
        <SubTypesMenu
          subTypes={subTypes}
          selected={selectedCR}
          onSelect={onSubtypeSelect}
        />
      </MobileOnly>
    </SubTypesWrapperView>
  );
};
